import React, { useEffect, useState } from 'react'
import BepClosedIcon from '../../../assets/bep-closed.svg'
import {
  StyledHeader,
  StyledIconButton,
  StyledMesssageImage,
  StyledMesssageImageWrapper,
  StyledHeaderButtonsGroup,
  StyledLabelHeaderWrapper,
  StyledMenu,
  StyledMenuItem,
  StyledInput
} from './styled'
import ExpandIcon from '../../../assets/ExpandIcon'
import ShrinkIcon from '../../../assets/ShrinkIcon'
import { useAppSelector } from '../../../reducers/hooks'
import { useDispatch } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import getForcedMobile from '../../../utils/forceMobile'
import { getIsIframe } from '../../../utils/getIsIframe'
import { iframeReducer } from '../../../reducers/iframe/iframeReducer'
import { FaRegPenToSquare, FaEllipsisVertical, FaRegTrashCan, FaPencil } from 'react-icons/fa6'
import { Menu as MenuIcon } from '@mui/icons-material'
import DeleteSessionConfirmation from '../../../components/ChatHistory/DeleteSessionConfirmation'
import { useChatHistory } from '../../../features/ChatHistory'
import { changeSessionId } from '../../../reducers/user/userReducer'
import { v4 as uuidv4 } from 'uuid'

interface ChatConversationHeaderProps {
  onOpenNewChat: () => void
  onMenuClick?: () => void
}

const ChatBotScreenHeader: React.FC<ChatConversationHeaderProps> = ({ onOpenNewChat, onMenuClick }): JSX.Element => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const forcedMobile = getForcedMobile()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isIframe = getIsIframe()
  const hasTouchCapability = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const { handleDeleteSession, saveRename } = useChatHistory()
  const onInitialScreen = useAppSelector((state) => state.user.showInitialScreen) ?? true
  const [showIcon, setShowIcon] = useState(true)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false)
  const chatbotExpanded = useAppSelector((state) => state.iframe.chatExpanded) ?? false
  const headerColor = localStorage.getItem('headerColor') === '' ? null : localStorage.getItem('headerColor')
  const hideExpandIcon = localStorage.getItem('hide_expand_icon') === 'true' ? true : ((!isMobile || !forcedMobile) && !isIframe) || (chatbotExpanded && !isMobile && !forcedMobile)
  const sessionId = useAppSelector((state) => state.user.sessionId)
  const chatHistory = useAppSelector((state) => state.user.chatHistory)
  const currentSession = useAppSelector((state) => state.user.currentSession)
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState('')
  const editHeaderInputRef = React.useRef<HTMLInputElement>(null)

  const closeDeleteModal = (): void => {
    setDeleteModalOpen(false)
    setSessionToDelete(null)
  }

  useEffect(() => {
    if (localStorage.getItem('showHeaderLogo') === 'false') {
      setShowIcon(false)
    }
  }, [])

  useEffect(() => {
    if (!isEditing) {
      setEditedName(chatHistory?.find((session) => session.session_id === sessionId)?.name ?? '')
    }
  }, [chatHistory, sessionId, isEditing])

  const onChangeChatbotSize = (): void => {
    chatbotExpanded ? dispatch(iframeReducer.actions.changeChatExpanded(false)) : dispatch(iframeReducer.actions.changeChatExpanded(true))
    chatbotExpanded ? parent.postMessage('shrinkChatbotWindow', '*') : parent.postMessage('expandChatbotWindow', '*')
    chatbotExpanded && window.isfromRN === true ? parent.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'shrinkChatbotWindow', data: '*' })) : parent.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'expandChatbotWindow', data: '*' }))
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const handleDelete = (sessionId: string): void => {
    setSessionToDelete(sessionId)
    setDeleteModalOpen(true)
    setAnchorEl(null)
    handleClose()
  }

  const handleDeleteConfirmed = (): void => {
    setDeleteModalOpen(false)
    dispatch(changeSessionId(chatHistory?.[0]?.session_id ?? uuidv4()))
  }

  const handleRename = async (sessionName: string, sessionId: string): Promise<void> => {
    await saveRename(sessionName, sessionId)
    setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing) {
      handleClose()
      setTimeout(() => {
        try {
          editHeaderInputRef.current?.focus()
          // move cursor to end
          const len = editHeaderInputRef.current?.value.length ?? 0
          editHeaderInputRef.current?.setSelectionRange(0, len)
        } catch (e) {
          // ignore
        }
      }, 0)
    }
  }, [isEditing])

  return (
    <StyledHeader isMobile={isMobile || forcedMobile} isHeaderColor={headerColor}>
      {(hasTouchCapability || forcedMobile) && onMenuClick !== undefined &&
        <StyledIconButton size="large" title='Open Chat History' className='hamburgerMenuButton' onClick={onMenuClick}>
          <MenuIcon fontSize="medium"/>
        </StyledIconButton>
      }
      {chatbotExpanded && !hideExpandIcon &&
        <StyledIconButton size="large" title='Minimize Chat Window' className='expandMinimizeButton' onClick={() => { onChangeChatbotSize() }}>
          <ShrinkIcon fontSize="medium"/>
        </StyledIconButton>
      }
      {!chatbotExpanded && !hideExpandIcon &&
        <StyledIconButton size="large" title='Expand Chat Window' className='expandMinimizeButton' onClick={() => { onChangeChatbotSize() }}>
          <ExpandIcon fontSize="medium"/>
        </StyledIconButton>
      }
      {onInitialScreen &&
      <StyledMesssageImageWrapper>
        {showIcon &&
        <StyledMesssageImage
          isMobile={isMobile || forcedMobile}
          src={BepClosedIcon}
          alt='Message Icon'
        />
        }
      </StyledMesssageImageWrapper>
      }
      {!onInitialScreen &&
        (isEditing
          ? <StyledInput ref={editHeaderInputRef} type="text" id='editHeaderNameInput' defaultValue={currentSession?.name} value={editedName} onChange={(e) => { setEditedName(e.currentTarget.value) }} onBlur={(e) => { handleRename(sessionId ?? '', editedName).catch(console.error) }} onKeyDown={(e) => { if (e.key === 'Enter') { handleRename(sessionId ?? '', editedName).catch(console.error) } }}/>
          : <StyledLabelHeaderWrapper
            title={currentSession?.name ?? 'Chat Conversation'}
          >
            {currentSession?.name ?? 'Chat Conversation'}
          </StyledLabelHeaderWrapper>
        )
      }
      {!onInitialScreen &&
      <StyledHeaderButtonsGroup>
        <StyledIconButton size="large" title='Start New Chat' onClick={onOpenNewChat}>
          <FaRegPenToSquare color='black' size={'24px'}/>
        </StyledIconButton>
        <StyledIconButton style={{ paddingLeft: '0px' }} size="large" title='Chat Actions' onClick={handleClick}>
          <FaEllipsisVertical color='black' size={'24px'}/>
        </StyledIconButton>
        <StyledMenu
          anchorEl={anchorEl}
          open={anchorEl !== null}
          onClose={handleClose}
          className='styledMenu'
        >
          <StyledMenuItem onClick={() => { setEditedName(currentSession?.name ?? ''); setIsEditing(true) }}><FaPencil /> Rename</StyledMenuItem>
          <StyledMenuItem className='deleteMenuItem' onClick={() => { handleDelete(currentSession?.session_id ?? '') }}><FaRegTrashCan /> Delete</StyledMenuItem>
        </StyledMenu>
      </StyledHeaderButtonsGroup>
      }
      <DeleteSessionConfirmation
        open={deleteModalOpen}
        sessionName={chatHistory?.find(s => s.session_id === sessionToDelete)?.name ?? null}
        onClose={closeDeleteModal}
        onConfirmation={() => { if (sessionToDelete !== null) { handleDeleteSession(sessionToDelete).catch(console.error); handleDeleteConfirmed() } }}
      />
    </StyledHeader>
  )
}

export default ChatBotScreenHeader
